import { UseFormReturn, FieldValues } from 'react-hook-form';
import { EntityRecord } from '../../api/api-entities';
import { Field, ConditionMode, ConditionOperator } from '../../schemas/entity';
import {
  BooleanFieldRenderer,
  NumberFieldRenderer,
  PickListFieldRenderer,
  PromptFieldRenderer,
  ObjectFieldRenderer,
  ReferenceFieldRenderer,
  StringFieldRenderer,
  YamlFieldRenderer,
  ConnectResourceFieldRenderer
} from './components/field-renderers';
import { DatetimeFieldRenderer } from './components/field-renderers/DateTimeFieldRenderer';

export type ItemFormMode = 'create' | 'edit';

interface ItemFormProps {
  entity: EntityRecord;
  form: UseFormReturn<FieldValues>;
}

function evaluateCondition(
  fieldValue: unknown,
  operator: ConditionOperator,
  expectedValue: boolean | string[]
): boolean {
  switch (operator) {
    case ConditionOperator.EQUALS:
      if (typeof expectedValue === 'boolean') {
        return fieldValue === expectedValue;
      }
      if (Array.isArray(expectedValue) && expectedValue.length > 0) {
        return fieldValue === expectedValue[0];
      }
      return false;
    case ConditionOperator.IN:
      return Array.isArray(expectedValue) && expectedValue.includes(String(fieldValue));
    default:
      return false;
  }
}

export function ItemForm({ entity, form }: ItemFormProps) {
  const fields = entity.schema.fields ?? {};
  const formValues = form.watch();

  const shouldShowField = (_fieldKey: string, field: Field): boolean => {
    if (!field.conditions) {
      return true;
    }

    if (field.conditions.groups.length === 0) {
      return true;
    }

    return field.conditions.groups.some((group) => {
      const results = group.conditions.map((condition) => {
        const fieldValue = formValues[condition.field];
        return evaluateCondition(fieldValue, condition.operator, condition.value);
      });

      return group.mode === ConditionMode.AND ? results.every(Boolean) : results.some(Boolean);
    });
  };

  const renderField = (key: string, field: Field) => {
    const definition = field.definition;
    const isRequired = field.definition.required;
    const label = `${key}${isRequired ? ' *' : ''}`;

    switch (definition.type) {
      case 'boolean':
        return (
          <BooleanFieldRenderer key={key} fieldKey={key} field={field} form={form} label={label} disabled={false} />
        );

      case 'number':
        return (
          <NumberFieldRenderer key={key} fieldKey={key} field={field} form={form} label={label} disabled={false} />
        );

      case 'string':
        return (
          <StringFieldRenderer key={key} fieldKey={key} field={field} form={form} label={label} disabled={false} />
        );

      case 'reference':
        return (
          <ReferenceFieldRenderer key={key} fieldKey={key} field={field} form={form} label={label} disabled={false} />
        );

      case 'picklist':
        return (
          <PickListFieldRenderer key={key} fieldKey={key} field={field} form={form} label={label} disabled={false} />
        );

      case 'prompt':
        return (
          <PromptFieldRenderer key={key} fieldKey={key} field={field} form={form} label={label} disabled={false} />
        );

      case 'yaml':
        return <YamlFieldRenderer key={key} fieldKey={key} field={field} form={form} label={label} disabled={false} />;

      case 'object':
        return (
          <ObjectFieldRenderer key={key} fieldKey={key} field={field} form={form} label={label} disabled={false} />
        );

      case 'date_time':
        return (
          <DatetimeFieldRenderer key={key} fieldKey={key} field={field} form={form} label={label} disabled={false} />
        );

      case 'connect_resource':
        return (
          <ConnectResourceFieldRenderer
            key={key}
            fieldKey={key}
            field={field}
            form={form}
            label={label}
            disabled={false}
          />
        );

      default:
        return null;
    }
  };

  const sortedFields = Object.entries(fields).sort(([, a], [, b]) => {
    const orderA = a.order ?? 0;
    const orderB = b.order ?? 0;
    const orderDiff = orderA - orderB;
    if (orderDiff !== 0) return orderDiff;
    return 0;
  });

  return (
    <div className='flex flex-col py-2'>
      {sortedFields.map(([key, field]) => {
        if (!shouldShowField(key, field)) {
          return null;
        }

        return (
          <div key={key} className='px-4 first:pt-0 last:pb-0 py-3'>
            {renderField(key, field)}
          </div>
        );
      })}
    </div>
  );
}
